import React, { useContext, useMemo, useState, createContext } from 'react';

import { useGetOrganizationLoanProductCreditFacilityGroupingsQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { LpToCfEntityGrouping } from '../ui/types';

export type LoanProductCreditFacilityGroupContextType = {
  lpToCfEntityGrouping: LpToCfEntityGrouping;
  setLpToCfEntityGrouping: React.Dispatch<
    React.SetStateAction<LpToCfEntityGrouping | null>
  >;
};

export const LoanProductCreditFacilityGroupContext = createContext<LoanProductCreditFacilityGroupContextType | null>(
  null,
);

export const useLoanProductCreditFacilityGroupContext = () => {
  const context = useContext(LoanProductCreditFacilityGroupContext);
  if (context === null) {
    throw new Error('LoanProductCreditFacilityGroupContext must be present');
  }

  return context;
};

export const LoanProductCreditFacilityGroupProvider: React.FC<{
  renderLoading: React.ReactElement;
  organizationId: string;
  fallbackComponent: React.ReactElement;
}> = ({ renderLoading, organizationId, children, fallbackComponent }) => {
  const [
    lpToCfEntityGrouping,
    setLpToCfEntityGrouping,
  ] = useState<LpToCfEntityGrouping | null>(null);
  const {
    data,
    loading,
  } = useGetOrganizationLoanProductCreditFacilityGroupingsQuery({
    variables: { organizationId },
  });

  const context = useMemo(() => {
    if (lpToCfEntityGrouping) {
      return {
        lpToCfEntityGrouping,
        setLpToCfEntityGrouping,
      };
    }

    if (data && data.organization.loanProductCreditFacilityGroups?.length > 0) {
      return {
        lpToCfEntityGrouping:
          data.organization.loanProductCreditFacilityGroups[0], // returning first index because we're assuming borrowers are single mesh, need to revisit for multi-mesh customers
        setLpToCfEntityGrouping,
      };
    }

    return null;
  }, [lpToCfEntityGrouping, setLpToCfEntityGrouping, data]);

  if (
    !loading &&
    data?.organization.loanProductCreditFacilityGroups &&
    data.organization.loanProductCreditFacilityGroups.length === 0
  ) {
    return fallbackComponent;
  }

  if (loading || !context) {
    return renderLoading;
  }
  return (
    <LoanProductCreditFacilityGroupContext.Provider value={context}>
      {children}
    </LoanProductCreditFacilityGroupContext.Provider>
  );
};
